import { css } from '@emotion/css';
import { forwardRef, useId, type HTMLProps } from 'react';

import { type GrafanaTheme2 } from '@grafana/data';
import { type StringSelector, selectors } from '@grafana/e2e-selectors';

import { useStyles2 } from '../../../themes/ThemeContext';
import { getFocusStyles, getInternalRadius, getMouseFocusStyles } from '../../../themes/mixins';
import { Tooltip } from '../../Tooltip/Tooltip';
import { getPropertiesForButtonSize } from '../commonStyles';

export const RADIO_GROUP_PADDING = 2;
export type RadioButtonSize = 'sm' | 'md';

export interface RadioButtonProps extends Omit<HTMLProps<HTMLInputElement>, 'size'> {
  size?: RadioButtonSize;
  disabled?: boolean;
  name?: string;
  description?: string;
  active: boolean;
  onChange: () => void;
  onClick: () => void;
  fullWidth?: boolean;
  title?: string;
  'aria-label'?: StringSelector;
  children?: React.ReactNode;
}

export const RadioButton = forwardRef<HTMLInputElement, RadioButtonProps>(
  (
    {
      children,
      active = false,
      disabled = false,
      size = 'md',
      onChange,
      onClick,
      name = undefined,
      description,
      fullWidth,
      title,
      'aria-label': ariaLabel,
      ...rest
    },
    ref
  ) => {
    const styles = useStyles2(getRadioButtonStyles, size, fullWidth);
    const id = useId();
    const adjustedTitle = title ?? ariaLabel;

    const inputRadioButton = (
      <input
        {...rest}
        type="radio"
        className={styles.radio}
        onChange={onChange}
        onClick={onClick}
        disabled={disabled}
        id={id}
        checked={active}
        name={name}
        aria-label={ariaLabel}
        title={adjustedTitle}
        ref={ref}
      />
    );
    return description ? (
      <div className={styles.radioOption} data-testid={selectors.components.RadioButton.container}>
        <Tooltip content={description} placement="bottom">
          {inputRadioButton}
        </Tooltip>
        <label className={styles.radioLabel} htmlFor={id} title={adjustedTitle}>
          {children}
        </label>
      </div>
    ) : (
      <div className={styles.radioOption} data-testid={selectors.components.RadioButton.container}>
        {inputRadioButton}
        <label className={styles.radioLabel} htmlFor={id} title={adjustedTitle}>
          {children}
        </label>
      </div>
    );
  }
);

RadioButton.displayName = 'RadioButton';

const getRadioButtonStyles = (theme: GrafanaTheme2, size: RadioButtonSize, fullWidth?: boolean) => {
  const { fontSize, height, padding } = getPropertiesForButtonSize(size, theme);
  const visualRefreshEnabled = theme.flags.visualDesignRefresh;

  const textColor = theme.colors.text.secondary;
  const textColorHover = theme.colors.text.primary;
  // remove the group inner padding (set on RadioButtonGroup)
  const labelHeight = height * theme.spacing.gridSize - 4 - 2;

  return {
    radioOption: css({
      display: 'flex',
      justifyContent: 'space-between',
      position: 'relative',
      flex: fullWidth ? '1 1 0' : '0 1 auto',
      minWidth: 0,
      textAlign: 'center',
    }),
    radio: css(
      {
        position: 'absolute',
        opacity: 0,
        zIndex: 2,
        width: '100% !important',
        height: '100%',
        cursor: 'pointer',

        '&:checked + label': {
          color: theme.colors.text.primary,
          fontWeight: theme.typography.fontWeightMedium,
          background: theme.colors.action.selected,
          zIndex: 1,
          // this ensures the selected radio button is shown when forced colors are active
          '@media (forced-colors: active)': {
            outline: '1px solid transparent',
          },
        },

        '&:focus + label, &:focus-visible + label': getFocusStyles(theme),

        '&:focus:not(:focus-visible) + label': getMouseFocusStyles(theme),

        '&:disabled + label': {
          color: theme.colors.text.disabled,
          cursor: 'not-allowed',
        },
      },
      visualRefreshEnabled && {
        '&:checked + label': {
          background: theme.colors.action.focus,
          border: `1px solid ${theme.colors.border.medium}`,
          color: theme.colors.accent.text,
          fontWeight: 'unset',
        },
      }
    ),
    radioLabel: css(
      {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize,
        height: `${labelHeight}px`,
        // Deduct border from line-height for perfect vertical centering on windows and linux
        lineHeight: `${labelHeight}px`,
        color: textColor,
        padding: theme.spacing(0, padding),
        borderRadius: getInternalRadius(theme, RADIO_GROUP_PADDING),
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap',
        flexGrow: 1,
        minWidth: 0,
        overflow: 'hidden',

        '&:hover': {
          color: textColorHover,
        },
      },
      visualRefreshEnabled && {
        border: '1px solid transparent',

        '@media (forced-colors: active)': {
          border: 'none',
        },
      }
    ),
  };
};
